import React, { useState, useRef } from 'react';
import { Field, Form } from 'react-final-form';
import { useI18n } from '@locales/client';

import { Condition } from '@/shared/ui/condition';
import { FormField } from '@/shared/ui/form/form-field';
import { CheckboxField } from '@/shared/ui/form/checkbox-field';
import { BankCardField } from '@/shared/ui/form/bank-card-field';
import { NumberInputField } from '@/shared/ui/form/number-input-field';
import { validateSchema } from '@/shared/libs/validate';
import { Box } from '@/shared/ui/box';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { formatCurrency } from '@/shared/libs/format-currency';
import {
  BodyTextBoldSecondary,
  BodyTextRegularSecondary,
} from '@/shared/ui/typography';

import { useWithdrawForm } from '@/features/wallet-managment/model/hooks';
import { createDynamicSchema } from '@/features/wallet-managment/model/schemas';
import {
  populateFieldsWithPreviousValues,
  hasTranslation,
} from '@/features/wallet-managment/model/utils';
import { CloseIconImage } from '@/features/auth-managment/styles';
import { useConfirmDataPrompt } from '@/features/data-managment/model/hooks';

import { MaxAmountButton, WithdrawButton } from '../../styles';
import { FormGroup } from '@/features/wallet-managment/styles';
import { GroupInput } from './styles';
import {
  BackIconImageWrapper,
  CarretRightIcon,
} from '../../../back-button/styles';
import { AdressesDetailFiat } from '../../../address-detail';
import { MethodUnavailable } from '../method-unavailable';
import { ExpireInput } from '@/shared/ui/form/expire-input';
import { BoxExpire } from '@/features/wallet-managment/ui/fiat-form/ui/card-form/styles';

export const WithdrawFiatForm = ({
  setCurrentStep,
  currentStep,
  onClose,
  setCurrentCurrency,
  settings,
  setCurrentTransaction,
}) => {
  const { createFiatPaymentTrigger, transformTransaction } =
    useWithdrawForm(null);
  const { removeQueryParam } = useConfirmDataPrompt();
  const { user } = useUser();
  const [form, setForm] = useState({
    amount: '',
    save: true,
  });
  const expireMonthRef = useRef<HTMLInputElement>(null);
  const expireYearRef = useRef<HTMLInputElement>(null);
  const t = useI18n();

  const currentSettings = settings?.methods.find((method) => {
    if (currentStep.includes(method.method)) {
      return method.method;
    }
  });

  const submitWithdraw = async (values) => {
    const { amount, ...fields } = values;

    const params = {
      amount: amount,
      method: currentSettings?.method,
      fields,
    };

    try {
      const transaction = await createFiatPaymentTrigger(params);
      const transformedTransaction = transformTransaction(
        transaction,
        t('code')
      );
      setCurrentTransaction(transformedTransaction);
      setCurrentStep('history-detail');
    } catch (error) {
      // console.error(error);
    }
  };

  const handleBackButton = (key: string) => () => {
    setCurrentCurrency(null);
    setCurrentStep(key);
    removeQueryParam('openPayment');
  };

  // Проверка доступности метода
  if (
    (+currentSettings?.minAmount === 0 && +currentSettings?.maxAmount === 0) ||
    +currentSettings?.minAmount > +currentSettings?.maxAmount
  ) {
    return (
      <FormGroup $marginTop="0">
        <Box $margin="auto" $height="100%">
          <Box onClick={onClose}>
            <CloseIconImage
              width={32}
              height={32}
              src="/images/icons/close-dialog.svg"
            />
          </Box>
          <BackIconImageWrapper onClick={handleBackButton('withdraw')}>
            <Box>
              <CarretRightIcon path="#AFAFAF" />
            </Box>
            <BodyTextRegularSecondary color="#AFAFAF">
              {t('wallet.back')}
            </BodyTextRegularSecondary>
          </BackIconImageWrapper>
          <MethodUnavailable
            limit={{
              max: +currentSettings.maxAmount,
              min: +currentSettings.minAmount,
            }}
            symbol={user.currencySymbol}
          />
        </Box>
      </FormGroup>
    );
  }

  const getSchema = () => {
    if (!currentSettings) return undefined;

    return createDynamicSchema(
      t,
      currentSettings.minAmount,
      currentSettings.maxAmount,
      currentSettings.fields
    );
  };

  const schema = getSchema();
  const validate = schema ? validateSchema(schema) : false;

  const generatePlaceholder = (fieldName: string): string => {
    if (hasTranslation(t, `wallet.fiat.withdraw_iban_form.${fieldName}`)) {
      return t(`wallet.fiat.withdraw_iban_form.${fieldName}`);
    }
    return t(`wallet.deposit.field_placeholder`, { fieldName });
  };

  const generateLabel = (fieldName: string): string => {
    if (hasTranslation(t, `wallet.withdraw.fields.${fieldName}`)) {
      return t(`wallet.withdraw.fields.${fieldName}`);
    }
    return fieldName;
  };
  const findMethodKey = currentSettings?.fields.find(
    (el) => el === 'card' || el === 'iban' || el === 'binpay'
  );

  return (
    <Form onSubmit={submitWithdraw} validate={validate} initialValues={form}>
      {(props) => {
        return (
          <FormGroup>
            <Box $display="flex" $flexDirection="column" $gap="24px">
              <Box $display="flex" $flexDirection="column" $gap="16px">
                <Box
                  $display="flex"
                  $justifyContent="center"
                  $gap={4}
                  $alignItems="center"
                >
                  <BodyTextRegularSecondary color="#AFAFAF">
                    {t('wallet.withdraw.min_amount')}:
                  </BodyTextRegularSecondary>
                  <BodyTextRegularSecondary>
                    {formatCurrency(currentSettings?.minAmount, {
                      currency: user.currency,
                      currencySymbol: user.currencySymbol,
                    })}
                  </BodyTextRegularSecondary>
                </Box>
                <Field
                  {...props}
                  name="amount"
                  type="number"
                  component={NumberInputField}
                />
              </Box>

              <Box>
                <MaxAmountButton>
                  <Box
                    $display="flex"
                    $gap={4}
                    $alignItems={'center'}
                    onClick={() =>
                      props.form.change('amount', currentSettings?.maxAmount)
                    }
                  >
                    <BodyTextRegularSecondary>
                      {formatCurrency(currentSettings?.maxAmount, {
                        currency: user.currency,
                        currencySymbol: user.currencySymbol,
                      })}
                    </BodyTextRegularSecondary>
                    <BodyTextBoldSecondary>
                      {t('wallet.withdraw.max')}
                    </BodyTextBoldSecondary>
                  </Box>
                </MaxAmountButton>
              </Box>
              <Box $textAlign="left">
                <AdressesDetailFiat
                  keyAddres={findMethodKey}
                  inputValue={props?.values}
                  onEdit={(address) =>
                    populateFieldsWithPreviousValues(props, address)
                  }
                />
              </Box>
            </Box>

            <Box>
              <Box onClick={onClose}>
                <CloseIconImage
                  width={32}
                  height={32}
                  src="/images/icons/close-dialog.svg"
                />
              </Box>
              <BackIconImageWrapper onClick={handleBackButton('withdraw')}>
                <Box>
                  <CarretRightIcon path="#AFAFAF" />
                </Box>
                <BodyTextRegularSecondary color="#AFAFAF">
                  {t('wallet.back')}
                </BodyTextRegularSecondary>
              </BackIconImageWrapper>

              <GroupInput>
                <Condition match={!!currentSettings?.fields}>
                  <Box $textAlign="center">
                    <BodyTextBoldSecondary>
                      {t('wallet.deposit.enter_data')}
                    </BodyTextBoldSecondary>
                  </Box>
                  {currentSettings?.fields?.map((fieldName) => {
                    const fieldComponents = {
                      card: {
                        Component: BankCardField,
                        mask: '9999 9999 9999 9999',
                      },
                      expire: {
                        customRender: (
                          <BoxExpire key={fieldName}>
                            <BodyTextBoldSecondary>
                              {t('wallet.fiat.input_fields.expire.label')}
                            </BodyTextBoldSecondary>
                            <Box $display="flex" $marginTop="5px">
                              <Field
                                name="expireMonth"
                                component={ExpireInput}
                                ref={expireMonthRef}
                                refNex={expireYearRef}
                                small
                                type="text"
                                placeholder={t(
                                  'wallet.fiat.input_fields.expire.placeholder_month'
                                )}
                              />
                              <Box $margin="16px 8px">/</Box>
                              <Field
                                name="expireYear"
                                component={ExpireInput}
                                ref={expireYearRef}
                                small
                                type="text"
                                placeholder={t(
                                  'wallet.fiat.input_fields.expire.placeholder_year'
                                )}
                              />
                            </Box>
                          </BoxExpire>
                        ),
                      },
                      default: {
                        Component: FormField,
                      },
                    };

                    const config =
                      fieldComponents[fieldName] || fieldComponents.default;

                    if (fieldName === 'expire') return config.customRender;

                    return (
                      <Field
                        key={fieldName}
                        name={fieldName}
                        mask={config.mask}
                        component={config.Component}
                        type="text"
                        label={
                          <BodyTextBoldSecondary>
                            {generateLabel(fieldName)}
                          </BodyTextBoldSecondary>
                        }
                        placeholder={generatePlaceholder(fieldName)}
                      />
                    );
                  })}
                </Condition>

                <Field
                  name="save"
                  component={CheckboxField}
                  type="checkbox"
                  label={
                    <BodyTextRegularSecondary>
                      {t('wallet.fiat.input_fields.sections.save')}
                    </BodyTextRegularSecondary>
                  }
                />
              </GroupInput>
              <Box
                $position="absolute"
                $bottom="18px"
                $borderRadius="8px"
                $opacity="1"
                $width="calc(100% - 36px)"
                $zIndex="100"
                $backgroundColor="#3D3C41"
              >
                <WithdrawButton
                  onClick={() => props.form.submit()}
                  type="submit"
                  disabled={props.invalid}
                  variant={props.invalid ? 'gray' : 'orangeGradient'}
                >
                  <BodyTextBoldSecondary>
                    {t('wallet.withdraw.withdraw.submit', {
                      amount: formatCurrency(props.values.amount, {
                        currency: user.currency,
                        currencySymbol: user.currencySymbol,
                      }),
                    })}
                  </BodyTextBoldSecondary>
                </WithdrawButton>
              </Box>
            </Box>
          </FormGroup>
        );
      }}
    </Form>
  );
};
